// V2 /app shell (BRIEFS C2.4, design _v2/design/Main.dc.html).
//
// - Title bar strip (app name + window buttons) ONLY inside the desktop wrapper
//   (window.v2desktop, C9.1). In a browser nothing is drawn there.
// - Top menu bar (C11.1, was a left menu): Home, Notes, Practice Test, Chapter Test, Revision
//   (D-27), Search; "All your work is saved"; the student's name with its initial. No log-out
//   anywhere. The page content uses the full window width.
// - fullScreen variant: no menu and no title bar (tests, revision, notes opened from them);
//   asks the desktop wrapper to go full screen while it is shown.
// - Desktop app (C9.1 FIX F1): the title bar stays on screen (sticky, top 0); the menu sits
//   just under it (deskBarH() = 44 px; 0 in a browser) and the notes strip under the menu
//   (pinnedTop()).
import React, { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAppAuth } from '../../context/AppAuthContext';
import { APP_NAME, C, FONT } from './theme';
import {
  IconBolt, IconCheck, IconChecklist, IconClose, IconHome, IconLeaf, IconMaximize,
  IconMinimize, IconNotes, IconRefresh, IconSearch, IconTarget,
} from './icons';

export const MENU = [
  { to: '/app', label: 'Home', Icon: IconHome, end: true },
  { to: '/app/notes', label: 'Notes', Icon: IconNotes },
  { to: '/app/practice', label: 'Practice Test', Icon: IconTarget },
  { to: '/app/chapter-test', label: 'Chapter Test', Icon: IconChecklist },
  { to: '/app/revision', label: 'Revision', Icon: IconBolt },
  { to: '/app/search', label: 'Search', Icon: IconSearch },
];

// Height of the desktop title bar; 0 in a normal browser (no window.v2desktop).
export const DESK_BAR = 44;
export function deskBarH() {
  return typeof window !== 'undefined' && window.v2desktop ? DESK_BAR : 0;
}

// Height of the top menu bar (C11.1) and where pinned page parts (the notes strip) stop:
// under the title bar (desktop app only) and the menu.
export const MENU_BAR = 52;
export function pinnedTop() {
  return deskBarH() + MENU_BAR;
}

const winBtn = {
  width: 46, height: 44, border: 0, background: 'transparent', color: '#FFFFFF',
  display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
  WebkitAppRegion: 'no-drag',
};

// Refresh (owner's request 2026-10-31): reload the page with the NEWEST version from the
// server, never a saved (cached) copy — first re-download the current address with
// cache 'reload' (that replaces the saved copy of the page, which names the new
// main.<hash>.js), then reload. No internet → the page stays as it is.
export async function refreshApp() {
  try {
    await fetch(window.location.href, { cache: 'reload', credentials: 'same-origin' });
    try { await fetch('/asset-manifest.json', { cache: 'reload' }); } catch { /* not needed */ }
  } catch {
    return false;                       // offline: keep the page
  }
  window.location.reload();
  return true;
}

function RefreshButton() {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const onClick = async () => {
    if (busy) return;
    setBusy(true);
    setNote('');
    const ok = await refreshApp();
    if (!ok) { setBusy(false); setNote('No internet'); }
  };
  return (
    <button type="button" aria-label="Refresh" title="Refresh — load the newest version"
      data-testid="v2-refresh" onClick={onClick} disabled={busy} style={{
        height: 30, margin: '0 8px 0 0', padding: '0 12px', border: '1px solid rgba(255,255,255,0.35)',
        borderRadius: 8, background: 'rgba(255,255,255,0.08)', color: '#FFFFFF', fontSize: 13, fontWeight: 500,
        display: 'inline-flex', alignItems: 'center', gap: 6, cursor: busy ? 'default' : 'pointer',
        WebkitAppRegion: 'no-drag', fontFamily: 'inherit',
      }}>
      <style>{'@keyframes v2spin{to{transform:rotate(360deg)}}'}</style>
      <span style={{ display: 'inline-flex', animation: busy ? 'v2spin 0.8s linear infinite' : 'none' }}><IconRefresh /></span>
      {busy ? 'Refreshing…' : (note || 'Refresh')}
    </button>
  );
}

export function TitleBar() {
  const desk = typeof window !== 'undefined' ? window.v2desktop : null;
  if (!desk) return null;
  const call = (fn) => () => { try { if (typeof desk[fn] === 'function') desk[fn](); } catch { /* ignore */ } };
  return (
    <div data-testid="v2-titlebar" style={{
      display: 'flex', alignItems: 'center', height: DESK_BAR, paddingLeft: 14, gap: 10,
      background: C.bar, color: '#FFFFFF', WebkitAppRegion: 'drag', userSelect: 'none', flexShrink: 0,
      // always on screen, above the page and its pop-ups (the video window, z 50, still covers it)
      position: 'sticky', top: 0, zIndex: 40,
    }}>
      <IconLeaf />
      <div style={{ flexGrow: 1, fontSize: 13, fontWeight: 500 }}>{APP_NAME}</div>
      <RefreshButton />
      <button type="button" aria-label="Minimize" style={winBtn} onClick={call('minimize')}><IconMinimize /></button>
      <button type="button" aria-label="Maximize" style={winBtn} onClick={call('toggleMaximize')}><IconMaximize /></button>
      <button type="button" aria-label="Close" style={winBtn} onClick={call('close')}><IconClose /></button>
    </div>
  );
}

// One item of the TOP menu bar (C11.1): icon + label, the active item underlined.
function MenuLink({ to, label, Icon, end }) {
  return (
    <NavLink to={to} end={end} data-testid={`v2-menu-${label.toLowerCase().replace(/\s+/g, '-')}`}
      style={({ isActive }) => ({
        display: 'flex', alignItems: 'center', gap: 8, height: MENU_BAR, padding: '0 14px',
        boxSizing: 'border-box', textDecoration: 'none', fontSize: 15, whiteSpace: 'nowrap', flexShrink: 0,
        color: isActive ? C.primaryDark : C.text,
        fontWeight: isActive ? 600 : 400,
        borderBottom: `3px solid ${isActive ? C.primary : 'transparent'}`,
        borderTop: '3px solid transparent',
      })}>
      <Icon />{label}
    </NavLink>
  );
}

function firstName(user) {
  const full = (user && (user.full_name || '')).trim();
  return full ? full.split(/\s+/)[0] : 'Student';
}

export default function AppLayout({ fullScreen = false }) {
  const { user } = useAppAuth();
  // The notes chapter page draws its own strips and columns edge to edge (C4.3).
  const { pathname } = useLocation();
  const flush = /^\/app\/notes\/[^/]+/.test(pathname);

  useEffect(() => {
    if (!fullScreen) return undefined;
    const desk = window.v2desktop;
    try { if (desk && desk.setFullScreen) desk.setFullScreen(true); } catch { /* ignore */ }
    return () => { try { if (desk && desk.setFullScreen) desk.setFullScreen(false); } catch { /* ignore */ } };
  }, [fullScreen]);

  if (fullScreen) {
    return (
      <div data-testid="v2-fullscreen" style={{
        minHeight: '100vh', background: C.bg, color: C.text, fontFamily: FONT.ui,
      }}>
        <Outlet />
      </div>
    );
  }

  const name = firstName(user);
  const initial = (firstName(user)[0] || 'S').toUpperCase();
  // C11.1: the menu is a bar across the top (one row), under the desktop title bar (D-64).
  // It stays on screen while a long page (the notes) scrolls; the notes strip sits under it
  // (pinnedTop()). Narrow windows: the bar scrolls sideways, it never wraps.
  const bar = deskBarH();

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', flexDirection: 'column',
      background: C.bg, color: C.text, fontFamily: FONT.ui,
    }}>
      <TitleBar />
      <nav aria-label="Main menu" data-testid="v2-topmenu" className="v2-topmenu" style={{
        position: 'sticky', top: bar, zIndex: 30, height: MENU_BAR, boxSizing: 'border-box',
        display: 'flex', flexWrap: 'nowrap', alignItems: 'stretch', gap: 2,
        padding: '0 16px', overflowX: 'auto', overflowY: 'hidden',
        background: C.menuBg, borderBottom: `1px solid ${C.border}`,
      }}>
        <style>{'.v2-topmenu::-webkit-scrollbar{height:4px}.v2-topmenu::-webkit-scrollbar-thumb{background:#CBD5E1;border-radius:2px}'}</style>
        {MENU.map((m) => <MenuLink key={m.to} {...m} />)}
        <div style={{ flexGrow: 1, minWidth: 16 }} />
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, padding: '0 12px',
          fontSize: 12, color: C.muted, whiteSpace: 'nowrap', flexShrink: 0,
        }}>
          <IconCheck />All your work is saved
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingLeft: 12, flexShrink: 0 }}>
          <div aria-hidden="true" style={{
            width: 32, height: 32, borderRadius: 16, background: C.primary, color: '#FFFFFF',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 14, fontWeight: 600, flexShrink: 0,
          }}>{initial}</div>
          <div data-testid="v2-user-name" style={{ fontSize: 14, fontWeight: 500, whiteSpace: 'nowrap' }}>{name}</div>
        </div>
      </nav>
      <main data-testid="v2-main" style={{
        flexGrow: 1, width: '100%', minWidth: 0, boxSizing: 'border-box',
        padding: flush ? 0 : '28px 32px 32px',
      }}>
        <Outlet />
      </main>
    </div>
  );
}
